import React, { useMemo, useRef } from 'react';
import { ReactComponent as JoyLogo } from '@assets/joy-logo.svg';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import { StyledUserMenu } from '@shared/components/StyledTopBar/StyledTopBar';
import { RoutesProvider, useLocation } from '@shared/core';
import { useEventRedirectEventMembershipsQuery } from '@graphql/generated';
import EventCardV2 from '@apps/accountMe/components/EventCardV2';
import queryString from '@shared/core/queryString';
import { getSubPath, mapDataToEventCards } from './utils';
import { useAuth } from '@shared/components/AuthProvider';
import { useAccountMeTelemetry } from '@apps/accountMe/AccountMe.telemetry';
import CreateEventButton from '@apps/accountMe/routes/Me/components/CreateEventButton/CreateEventButton';

const Root = () => {
  const { trackEventRedirectRoute } = useAccountMeTelemetry();
  const { loading, data } = useEventRedirectEventMembershipsQuery();
  const location = useLocation();
  const subPathRef = useRef(getSubPath(location));

  const eventCards = useMemo(() => {
    return mapDataToEventCards(data, subPathRef.current);
  }, [data]);

  if (loading) {
    return <JoyLogoLoader loaderKey="event-redirect-query" />;
  }

  // If no events, redirect user to create wedding
  if (!eventCards.length) {
    trackEventRedirectRoute('noEvents', subPathRef.current);
    const redirectUrl = new URL(`/createwedding`, window.location.origin);

    redirectUrl.searchParams.set('destination', subPathRef.current);

    const maybeScenarioQueryParam = queryString.getValueString('scenario');
    if (maybeScenarioQueryParam) {
      redirectUrl.searchParams.set('scenario', maybeScenarioQueryParam);
    }
    window.location.href = redirectUrl.pathname + redirectUrl.search;
    return null;
  }

  // If only one event, redirect automatically
  if (eventCards.length === 1) {
    trackEventRedirectRoute('singleEvent', subPathRef.current);
    window.location.href = eventCards[0].eventLink;
    return null;
  }

  return (
    <RoutesProvider>
      <Flex flexDirection="column" borderBottom={'1px solid'} borderBottomColor="mono3">
        <Flex paddingY={5} paddingX={6} height={72} justifyContent="space-between">
          <Box>
            <JoyLogo />
          </Box>

          <StyledUserMenu disableAccountInfoRedirect disableManageEventsRedirect />
        </Flex>
      </Flex>
      <Flex flexDirection="column" rowGap={7} maxWidth={1280} marginX={[null, null, 'auto']} paddingX={7} paddingY={[7, null, 8]}>
        <TextV2 typographyVariant={['hed6', null, 'display1']} fontWeight="semibold">
          Pick Your Event
        </TextV2>

        <Box display="grid" gridTemplateColumns={['minmax(0, 1fr)', null, 'repeat(3, minmax(0, 1fr))']} gap={7}>
          {eventCards.map(eventCard => (
            <EventCardV2 key={eventCard.id} eventCard={eventCard} useEventLinkAsIs performFullPageRedirect />
          ))}
        </Box>
      </Flex>
      <Flex
        flexDirection="column"
        rowGap={7}
        marginTop={{ _: 6, sm2: 8 }}
        width={1280}
        maxWidth="100%"
        marginX={[null, null, 'auto']}
        paddingX={7}
        paddingY={[7, null, 8]}
        alignItems="flex-start"
        marginBottom={10}
      >
        <TextV2 typographyVariant={['hed6', null, 'display1']} fontWeight="semibold">
          Or create a new one
        </TextV2>

        <CreateEventButton redirectDestination={subPathRef.current} />
      </Flex>
    </RoutesProvider>
  );
};

const EventRedirect = () => {
  const { isCurrentUserSettled } = useAuth();

  if (!isCurrentUserSettled) {
    // Allow user auth to settle first before making GQL call
    return <JoyLogoLoader loaderKey="admin-event-redirect" />;
  }

  return <Root />;
};

export default EventRedirect;
